/**
 * Wave CM1 — "Request a call back" for portal clients. Sends a portal message
 * the firm sees in its Portal Inbox (and by email). Hidden when the firm has
 * portal calls turned off.
 */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PhoneCallback } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { getPortalToken } from '../../lib/authToken';

interface Props {
  cases?: { id: string; title?: string; caseNumber?: string }[];
  /** Rendered inside the portal's AppBar → inherit colour. */
  color?: 'inherit' | 'primary';
}

const PortalCallbackButton: React.FC<Props> = ({ cases = [], color = 'inherit' }) => {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [caseId, setCaseId] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch('/api/portal/calls/config', { headers: { Authorization: `Bearer ${getPortalToken()}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive) setEnabled(Boolean(j?.data?.portalCalls)); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  if (!enabled) return null;

  const send = async () => {
    setSending(true);
    try {
      const res = await fetch('/api/portal/calls/callback-request', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getPortalToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ caseId: caseId || undefined, preferredTime: preferredTime || undefined, note: note || undefined }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || 'Could not send your request');
      toast.success(json?.data?.alreadyRequested
        ? 'Your legal team already has your call-back request.'
        : 'Request sent. Keep the portal open to receive the call here, or they may call your phone.');
      setOpen(false);
      setNote('');
      setPreferredTime('');
    } catch (err) {
      toast.error((err as Error)?.message || 'Could not send your request');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button color={color} startIcon={<PhoneCallback />} onClick={() => setOpen(true)} sx={{ mr: 1 }}>
        Request a call back
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Request a call back</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your legal team will be notified. They can call you right here in the portal while you are signed in.
          </Typography>
          <Stack spacing={2}>
            {cases.length > 0 && (
              <FormControl fullWidth size="small">
                <InputLabel>About</InputLabel>
                <Select label="About" value={caseId} onChange={(e) => setCaseId(String(e.target.value))}>
                  <MenuItem value="">General question</MenuItem>
                  {cases.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.title || c.caseNumber || 'My matter'}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              size="small"
              label="Best time to reach you (optional)"
              placeholder="e.g. weekdays after 3 pm"
              value={preferredTime}
              onChange={(e) => setPreferredTime(e.target.value)}
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              label="What is it about? (optional)"
              multiline
              minRows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: 1000 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={send} disabled={sending}>{sending ? 'Sending…' : 'Send request'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PortalCallbackButton;
